import { ClickAwayListener, Popper, TextField } from '@mui/material';
import classNames from 'classnames';
import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '../../../../../Contexts/Localization';
import { GMInputWrapper } from '../../../InputWrapper';
import css from './Select2.module.scss';
import { GMTextField } from '../../../TextField';
import { Close } from '@mui/icons-material';
import { GMList, GMListItem } from '../../../../Lists/List';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import React from 'react';
import { GMSvgIcon } from '../../../../SvgIcon/SvgIcon';
import { GMIconButton } from '../../../../Buttons/IconButton';
import { GMOutlinedInputLabel } from '../../../InputLabel';
import { useIsTablet } from '../../../../../CommonUtils/MediaQueries';
import { pagePaddingPx, sidebarWidthPx } from '../../../../../Themes/Contants';
import { ErrorLevel } from '../../../../../API/Enums';
import { GMSelect2Props, GMSelectOption, SelectCloseReason } from '../..';

/**
 * Select where the options list is a popper with the option to filter it
 */
export const Select2: React.FC<GMSelect2Props> = memo(({
    name,
    value,
    options,
    onChange,
    onBlur,
    onFocus,
    onClose: propsOnClose,
    emptyOption,
    label,
    placeholder,
    shrinkLabel,
    helperText,
    filterable,
    useMenuPortal,
    clearable = false,
    ...wrapperProps
}) => {
    const Localization = useLocalization();
    const [open, setOpen] = useState<boolean>(false);
    const textfieldRef = useRef<HTMLInputElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const menuWidth = useRef<number | undefined>(undefined);
    const [filter, setFilter] = useState<string>('');
    const [measuredWidth, setMeasuredWidth] = useState<number>(0);
    const isTablet = useIsTablet();

    const filteredOptions = useMemo<GMSelectOption[]>(() => {
        return options.filter((o) => o.label?.toLowerCase()
            .includes(filter?.trim()
                .toLowerCase()));
    }, [options, filter]);

    const clearFilter = useCallback(() => {
        setFilter('');
    }, []);

    const onClick = useCallback(() => {
        menuWidth.current = textfieldRef.current?.clientWidth;
        setOpen(true);
    }, []);

    const onClose = useCallback((reason: SelectCloseReason, event?: MouseEvent | TouchEvent) => {
        setOpen(false);
        clearFilter();

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (propsOnClose) {
            propsOnClose(reason);
        }
    }, [clearFilter, propsOnClose]);

    const onSelectChange = useCallback((value: string) => {
        onChange(value);
        onClose('selection');
        inputRef.current && inputRef.current.focus();
    }, [onChange, onClose]);

    const valueLabel: string = options.find((o) => o.value === value)?.label ?? '';

    useLayoutEffect(() => {
        if (inputRef.current) {
            const style: CSSStyleDeclaration = window.getComputedStyle(inputRef.current);
            const canvas: HTMLCanvasElement = document.createElement('canvas');
            const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
            if (ctx) {
                ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
                setMeasuredWidth(ctx.measureText(valueLabel).width);
            }
        }
    }, [valueLabel]);

    // Pad by a few pixels to absorb canvas/DOM subpixel rounding so wide glyphs (W, M, etc.) don't ellipsize.
    const width: string | undefined = measuredWidth ? `${Math.ceil(measuredWidth) + 4}px` : '100%';

    return (
        <>
            <GMInputWrapper {...wrapperProps}>
                {wrapperProps.variant === 'outlined' ?
                    <GMOutlinedInputLabel
                        label={label}
                        required={wrapperProps.required}
                        error={wrapperProps.error}
                        disabled={wrapperProps.disabled}
                        color={wrapperProps.color}
                    /> : null
                }
                <TextField
                    ref={textfieldRef}
                    variant={wrapperProps.variant}
                    name={name}
                    label={wrapperProps.variant === 'standard' ? label : undefined}
                    InputLabelProps={{
                        shrink: wrapperProps.hideShrinkLabel && !value ? false : shrinkLabel ? true : undefined,
                    }}
                    placeholder={placeholder}
                    value={valueLabel}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    disabled={wrapperProps.disabled}
                    InputProps={{
                        readOnly: true,
                        className: classNames(css.Input, {
                            [css.disabled]: !!wrapperProps.disabled,
                        }),
                        endAdornment:
                            <>
                                {clearable && value && !wrapperProps.disabled ?
                                    <GMIconButton
                                        tooltip={Localization.Clear}
                                        color={wrapperProps.color}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onChange('');
                                        }}
                                    >
                                        <Close />
                                    </GMIconButton>
                                    : null}
                                <GMSvgIcon
                                    color={wrapperProps.color}
                                    disabled={wrapperProps.disabled}
                                >
                                    {open ?
                                        <ArrowDropUpIcon /> :
                                        <ArrowDropDownIcon />
                                    }
                                </GMSvgIcon>
                            </>,

                    }}
                    inputProps={{
                        ref: inputRef,
                        className: classNames(
                            css.input,
                            wrapperProps.disabled ? css.disabled : undefined,
                        ),
                        size: 1,
                        style: { width: `max(100%, ${width})` },
                    }}
                    onKeyDown={(event) => event.key !== 'Tab' && !wrapperProps.disabled && onClick()}
                    onClick={(e) => !wrapperProps.disabled && onClick()}
                    error={wrapperProps.error !== undefined && wrapperProps.error !== ErrorLevel.None}
                    required={wrapperProps.required}
                    helperText={helperText}
                />
            </GMInputWrapper >
            <Popper
                className={css.popper}
                placement={'bottom-start'}
                open={open}
                anchorEl={textfieldRef.current}
                style={{
                    minWidth: menuWidth.current,
                    maxWidth: `calc(100vw - (${!isTablet ? `${sidebarWidthPx}px` : '0px'} + (2 * ${pagePaddingPx}px)))`,
                }}
                modifiers={[
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: {
                                left: (!isTablet ? sidebarWidthPx : 0) + pagePaddingPx,
                                right: pagePaddingPx,
                            },
                            boundary: window,
                        },
                    },
                ]}
                disablePortal={!useMenuPortal}
            >
                <ClickAwayListener onClickAway={(e) => onClose('close', e)}>
                    <div className={classNames(css.menu)}>
                        {filterable ?
                            <div className={css.filter}>
                                <GMTextField
                                    id={`select-filter-${label}-${name}`}
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder={Localization.TypeToFilter}
                                    variant={'outlined'}
                                    autofocus
                                    autoComplete={'off'}
                                    endAdornment={filter ? {
                                        icon: <Close />,
                                        tooltip: Localization.Clear,
                                        onClick: clearFilter,
                                    } : undefined}
                                />
                            </div> : null
                        }
                        <div className={css.list}>
                            <GMList noBorders>
                                {!filter && emptyOption ?
                                    <GMListItem
                                        selected={valueLabel === ''}
                                        onClick={() => onSelectChange('')}
                                        thin
                                    >
                                        <em>{Localization.None}</em>
                                    </GMListItem> : null
                                }
                                {filteredOptions.length === 0 ?
                                    <div className={css.noOptionsLabel}>
                                        {Localization.NoOptions}
                                    </div> : null
                                }
                                {filteredOptions.map((option: GMSelectOption, index: number) => {
                                    return (
                                        <GMListItem
                                            selected={option.value === value}
                                            key={`select-list-${name}-${index}`}
                                            onClick={() => onSelectChange(option.value)}
                                            disabled={option.disabled || wrapperProps.disabled}
                                            thin
                                        >
                                            {option.menuOptionLabel ? option.menuOptionLabel : option.label}
                                        </GMListItem>
                                    );
                                })}
                            </GMList>
                        </div>
                    </div>
                </ClickAwayListener>
            </Popper>
        </>
    );
});

export default Select2;